import { Divider, ListItemIcon, ListItemText, Menu, MenuItem, styled } from '@mui/material';
import { Fragment, type MouseEvent } from 'react';

import { MoreActionsIcon } from '../../../icons';
import type { AppShellAccount, AppShellAccountMenuItem } from './useAppShell';

type Props = {
  account: AppShellAccount;
  menuLabel: string;
  buttonId: string;
  menuId: string;
  anchor: HTMLElement | null;
  isOpen: boolean;
  onOpen: (event: MouseEvent<HTMLElement>) => void;
  onClose: () => void;
  onItemClick: (item: AppShellAccountMenuItem, event: MouseEvent<HTMLElement>) => void;
};

const AVATAR_SIZE = 36;

const blockStyles = {
  display: 'grid',
  alignItems: 'center',
  width: '100%',
} as const;

const AccountButton = styled('button')(({ theme }) => ({
  ...blockStyles,
  gridTemplateColumns: `${AVATAR_SIZE}px minmax(0, 1fr) auto`,
  columnGap: theme.spacing(1.5),
  minHeight: theme.spacing(7),
  margin: 0,
  padding: theme.spacing(1),
  border: 0,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: 'transparent',
  color: theme.palette.text.primary,
  font: 'inherit',
  textAlign: 'left',
  cursor: 'pointer',
  '@media (hover: hover)': {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  '&[aria-expanded="true"]': {
    backgroundColor: theme.palette.action.selected,
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2,
  },
}));

const AccountRow = styled('div')(({ theme }) => ({
  ...blockStyles,
  gridTemplateColumns: 'auto minmax(0, 1fr) auto',
  columnGap: theme.spacing(1.5),
  minHeight: theme.spacing(5.5),
  paddingInline: theme.spacing(1),
}));

const Avatar = styled('span')(({ theme }) => ({
  ...theme.typography.body2Emphasis,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: AVATAR_SIZE,
  height: AVATAR_SIZE,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  textTransform: 'uppercase',
}));

const Text = styled('span')({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
});

const Label = styled('span')(({ theme }) => ({
  ...theme.typography.body2Emphasis,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const Detail = styled('span')(({ theme }) => ({
  ...theme.typography.body2,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: theme.palette.text.secondary,
}));

const Trailing = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  color: theme.palette.text.secondary,
}));

/**
 * Signed-in user at the bottom of the `AppShell` sidebar: initials, label and role.
 * With `menuItems` the whole block opens the account menu; otherwise `actions` sit next to the label.
 */
export function AppShellAccountBlock(props: Props) {
  const content = (
    <>
      {props.account.initials ? <Avatar aria-hidden="true">{props.account.initials}</Avatar> : <span />}
      <Text>
        <Label title={props.account.label}>{props.account.label}</Label>
        {props.account.detail ? <Detail>{props.account.detail}</Detail> : null}
      </Text>
    </>
  );

  if (!props.account.menuItems) {
    return (
      <AccountRow>
        {content}
        {props.account.actions ? <Trailing>{props.account.actions}</Trailing> : <span />}
      </AccountRow>
    );
  }

  return (
    <>
      <AccountButton
        type="button"
        id={props.buttonId}
        aria-haspopup="true"
        aria-controls={props.isOpen ? props.menuId : undefined}
        aria-expanded={props.isOpen ? 'true' : 'false'}
        aria-label={`${props.menuLabel} : ${props.account.label}`}
        onClick={props.onOpen}
      >
        {content}
        <Trailing aria-hidden="true">
          <MoreActionsIcon fontSize="small" />
        </Trailing>
      </AccountButton>

      <Menu
        id={props.menuId}
        anchorEl={props.anchor}
        open={props.isOpen}
        onClose={props.onClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          list: { 'aria-labelledby': props.buttonId },
          paper: { style: { minWidth: props.anchor?.offsetWidth } },
        }}
      >
        {props.account.menuItems.map((item) => (
          <Fragment key={item.id}>
            {item.dividerBefore ? <Divider component="li" /> : null}
            <MenuItem
              {...(item.href ? { component: 'a', href: item.href } : {})}
              onClick={(event: MouseEvent<HTMLElement>) => props.onItemClick(item, event)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText>{item.label}</ListItemText>
            </MenuItem>
          </Fragment>
        ))}
      </Menu>
    </>
  );
}
